/**
 * Autores — la segunda sección del Editor Studio (11-sep-2026).
 *
 * Un escritor firma con su nombre la novela literaria y con un seudónimo el thriller, y cada firma
 * tiene su voz, sus referencias y sus líneas rojas. Aquí se registran las firmas y cada una tiene
 * su perfil: el cuestionario de dos rondas (quién eres; límites, referencias y hábitos) que conduce
 * el colaborador de perfil en el chat.
 *
 * Lo que se dice en voz alta, igual que en Proyectos: quitar una firma de la lista NO borra su
 * carpeta ni su perfil. Y un perfil que el autor borre o rehaga con el Finder se ve tal cual está
 * en el disco, porque no se recuerda: se mira.
 */
import { useCallback, useEffect, useState } from "react";
import { autores as listarAutores, crearAutor, olvidarAutor, pedirEncargo, traerPerfil } from "../api";
import type { Autor } from "../api";
import Preferencias from "./Preferencias";
import { useFlujo } from "../flujo";

const dia = (iso: string | null) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "—";
  }
};

const ESTADO: Record<Autor["perfil"]["estado"], [string, string]> = {
  completo: ["perfil hecho", "baja"],
  sin_perfil: ["sin perfil", "media"],
  sin_carpeta: ["la carpeta ya no está", "alta"],
};

export default function Autores({ carpeta, alPerfil }: { carpeta: string; alPerfil: (a: Autor) => void }) {
  const [lista, setLista] = useState<Autor[] | null>(null);
  const [nombre, setNombre] = useState("");
  const [seudonimos, setSeudonimos] = useState("");
  const [fallo, setFallo] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [confirmar, setConfirmar] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const flujo = useFlujo();

  const mirar = useCallback(async () => {
    const d = await listarAutores();
    setLista(d.autores);
  }, []);

  useEffect(() => {
    mirar().catch((e) => setFallo(/404/.test(String((e as Error)?.message))
      ? "El HumanInk de este ordenador es anterior a esta pantalla. Actualízalo desde Ajustes y vuelve."
      : (e as Error).message));
  }, [mirar]);

  async function nueva() {
    if (!nombre.trim()) return;
    setOcupado(true); setFallo(null); setAviso(null);
    try {
      const a = await crearAutor({ nombre: nombre.trim(), seudonimos });
      setNombre(""); setSeudonimos("");
      setAviso(a.creada
        ? `Firma creada. Su carpeta es ${a.carpeta}: ahí vivirá su perfil.`
        : `Firma creada sobre ${a.carpeta}.`);
      await mirar();
    } catch (e) {
      setFallo((e as Error).message);
    } finally {
      setOcupado(false);
    }
  }

  // El chat guarda el perfil en la carpeta del proyecto abierto: esto lo trae a la de la firma.
  // 25-sep: el chat no escribe ficheros, y el perfil se quedaba en la conversación mientras «Traer el
  // perfil» decía que no había ninguno. Esto lo escribe el motor con la plantilla de la skill y las
  // respuestas de la entrevista, en la carpeta del proyecto abierto.
  async function escribir() {
    if (!carpeta) return;
    setFallo(null); setAviso(null);
    try {
      await pedirEncargo("perfil-autor", carpeta, null);
      setAviso("Escribiendo tu perfil con la entrevista del chat, en segundo plano (un par de minutos). Cuando esté, pulsa «Traer el perfil del proyecto abierto».");
    } catch (e) { setFallo((e as Error).message); }
  }

  async function traer(a: Autor) {
    setFallo(null); setAviso(null);
    try {
      const r = await traerPerfil(a.id, carpeta);
      const nombre = r.traido.split(/[/\\]/).pop();
      setAviso(r.igual
        ? `Ese perfil ya estaba en la carpeta de «${a.nombre}»: no se ha duplicado.`
        : r.renombrado
          ? `«${a.nombre}» ya tenía un perfil distinto: el nuevo se ha guardado como «${nombre}» y el anterior sigue intacto.`
          : `Perfil traído a la carpeta de «${a.nombre}». El del proyecto sigue donde estaba.`);
      await mirar();
    } catch (e) {
      setFallo((e as Error).message);
    }
  }

  async function quitar(a: Autor) {
    setFallo(null);
    try {
      const r = await olvidarAutor(a.id);
      setConfirmar(null);
      setAviso(`«${a.nombre}» ya no está en la lista. Su carpeta y su perfil siguen en ${r.carpeta}: no se ha borrado nada.`);
      await mirar();
    } catch (e) {
      setFallo((e as Error).message);
    }
  }

  return (
    <>
      <section className="carta">
        <h2>Tus firmas
          <span className="sub">{lista === null ? "…" : lista.length === 1 ? "1 firma" : `${lista.length} firmas`}</span>
        </h2>

        {fallo && <p className="vacio" style={{ color: "var(--acc)" }}>{fallo}</p>}
        {aviso && <p className="vacio">{aviso}</p>}

        {lista !== null && !lista.length && !fallo && (
          <p className="vacio">
            Todavía no hay ninguna. Una firma es el nombre con el que sale un libro: el tuyo, o un
            seudónimo. Cada una tiene su perfil, porque la voz de un thriller no es la de un ensayo.
          </p>
        )}

        {!!lista?.length && (
          <div className="issues">
            {lista.map((a) => {
              const [texto, grado] = ESTADO[a.perfil.estado];
              return (
                <div className="issue" key={a.id}>
                  <p>
                    <b>{a.nombre}</b>
                    <span className={`grado ${grado}`} style={{ marginLeft: 8 }}>{texto}</span>
                  </p>
                  {!!a.seudonimos.length && <p className="vacio">También firma como {a.seudonimos.join(", ")}.</p>}
                  <p className="vacio mono">{a.carpeta}</p>
                  <p className="vacio">
                    {a.perfil.estado === "completo"
                      ? <>Perfil del {dia(a.perfil.actualizado)} · {a.perfil.docx?.split(/[/\\]/).pop()}
                          {a.perfil.versiones && a.perfil.versiones > 1 ? ` (hay ${a.perfil.versiones}, se usa el más reciente)` : ""}.</>
                      : a.perfil.estado === "sin_perfil"
                        ? <>Sin perfil todavía. Son dos rondas de preguntas, unos diez minutos, y a partir de ahí
                            todos los colaboradores saben qué no tocar.</>
                        : <>Estaba aquí y hoy no está. Puede que la hayas movido, o que viviera en un disco que no está conectado.</>}
                  </p>
                  {/* M-1: lo que deja hacer con su texto. Con un motor que todavía no lo guarda, en el flujo
                      se dice que llega con el siguiente; en el Studio de siempre no se promete nada. */}
                  {a.preferencias
                    ? <Preferencias autor={a} alGuardar={() => { mirar().catch(() => {}); }} />
                    : flujo && <p className="vacio">Aquí dirás qué dejas hacer con tu texto (poda, cambios de estilo, texto nuevo de la IA y lo que no se toca). Llega con la próxima versión del motor.</p>}
                  <div className="acciones">
                    {a.perfil.estado !== "sin_carpeta" && (
                      <button className={a.perfil.estado === "sin_perfil" ? "btn" : "btn fantasma"} onClick={() => alPerfil(a)}>
                        {a.perfil.estado === "sin_perfil" ? "Hacer el perfil" : "Rehacer el perfil"}
                      </button>
                    )}
                    {a.perfil.estado !== "sin_carpeta" && carpeta && (
                      <button className="btn fantasma" onClick={escribir}>Escribir el perfil con la entrevista</button>
                    )}
                    {a.perfil.estado !== "sin_carpeta" && carpeta && (
                      <button className="btn fantasma" onClick={() => traer(a)}>Traer el perfil del proyecto abierto</button>
                    )}
                    {confirmar === a.id ? (
                      <>
                        <button className="btn" onClick={() => setConfirmar(null)}>No, dejarla</button>
                        <button className="btn fantasma" onClick={() => quitar(a)}>Sí, quitar de la lista</button>
                      </>
                    ) : (
                      <button className="btn texto" onClick={() => { setConfirmar(a.id); setAviso(null); }}>Quitar</button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!!lista?.length && (
          <p className="vacio">
            Quitar una firma solo la saca de esta lista. <b>No borra su carpeta ni su perfil</b>: sus
            respuestas son confidenciales y se quedan en tu disco.
          </p>
        )}
      </section>

      <section className="carta">
        <h2>Nueva firma<span className="sub">un nombre, y sus seudónimos si los agrupa</span></h2>
        {/* 17-sep (Rais): un campo de lado a lado obliga al ojo a cruzar la pantalla desde la etiqueta
            hasta el botón. El formulario se ciñe a una medida y el botón va debajo, junto a lo que hace. */}
        <div className="formulario">
          <label className="campo">
            <span>Nombre</span>
            <div className="dir">
              <input className="escribir" placeholder="El nombre con el que firmas"
                     value={nombre} onChange={(e) => setNombre(e.target.value)}
                     onKeyDown={(e) => { if (e.key === "Enter") nueva(); }} />
            </div>
          </label>
          <label className="campo">
            <span>Seudónimos <i>(opcional)</i></span>
            <div className="dir">
              <input className="escribir" placeholder="Separados por comas"
                     value={seudonimos} onChange={(e) => setSeudonimos(e.target.value)}
                     onKeyDown={(e) => { if (e.key === "Enter") nueva(); }} />
            </div>
          </label>
          <div className="pie-form">
            <p className="vacio">Se creará su carpeta en <b className="mono">~/HumanInk/Autores/</b>.</p>
            <button className="btn" onClick={nueva} disabled={!nombre.trim() || ocupado}>
              {ocupado ? "Un momento…" : "Crear la firma"}
            </button>
          </div>
        </div>
      </section>
    </>
  );
}
